import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { WorkshopProvider } from "@/hooks/useWorkshop";
import { JurisdictionProvider } from "@/hooks/useJurisdiction";
import { AppLayout } from "@/components/layout/AppLayout";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { ChunkErrorBoundary } from "@/components/ChunkErrorBoundary";
import { PublicPageSkeleton } from "@/components/public/PublicPageSkeleton";

// Lazy-loaded pages
const Index = lazy(() => import("./pages/Index"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Companies = lazy(() => import("./pages/Companies"));

const Customers = lazy(() => import("./pages/Customers"));
const Storage = lazy(() => import("./pages/Storage"));
const StorageDetail = lazy(() => import("./pages/StorageDetail"));
const TireSets = lazy(() => import("./pages/TireSets"));
const TireSetDetail = lazy(() => import("./pages/TireSetDetail"));
const Bookings = lazy(() => import("./pages/Bookings"));
const Reports = lazy(() => import("./pages/Reports"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PublicBooking = lazy(() => import("./pages/PublicBooking"));
const PublicBookingEmbed = lazy(() => import("./pages/PublicBookingEmbed"));
const OnboardingSetup = lazy(() => import("./pages/OnboardingSetup"));
const NewCompany = lazy(() => import("./pages/NewCompany"));
const BookingSettings = lazy(() => import("./pages/BookingSettings"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));

const Scanner = lazy(() => import("./pages/Scanner"));
const Pricing = lazy(() => import("./pages/Pricing"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const CheckoutCancelled = lazy(() => import("./pages/CheckoutCancelled"));
const TireHotelInfo = lazy(() => import("./pages/TireHotelInfo"));
const BookingInfo = lazy(() => import("./pages/BookingInfo"));
const Settings = lazy(() => import("./pages/Settings"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const Invitation = lazy(() => import("./pages/Invitation"));
const DataRequest = lazy(() => import("./pages/DataRequest"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const OutreachUnsubscribe = lazy(() => import("./pages/OutreachUnsubscribe"));
const SubscriptionPaused = lazy(() => import("./pages/SubscriptionPaused"));

// A global staleTime keeps freshly-fetched data from refetching on every mount
// and window focus (the dashboard alone mounts ~6 queries). Correctness is still
// maintained by explicit invalidation on mutations and by realtime channels;
// per-query overrides (e.g. useSubscriptionStatus) still apply.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
    },
  },
});

const PageLoader = () => (
  <div className="flex h-screen w-full items-center justify-center">
    <div className="relative w-10 h-10">
      <div className="absolute inset-0 rounded-full border-2 border-accent/20" />
      <div className="absolute inset-0 rounded-full border-2 border-accent border-t-transparent animate-spin" />
    </div>
  </div>
);

// Per-route Suspense wrapper: shows a layout-matching skeleton (with the
// real PublicNav) instead of a blank spinner while the page chunk loads.
// Eliminates the perceived delay on first navigation to a public page.
const WithPublicSkeleton = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PublicPageSkeleton />}>{children}</Suspense>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <JurisdictionProvider>
      <WorkshopProvider>
        <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ChunkErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
              {/* Public routes (no sidebar) */}
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<WithPublicSkeleton><Auth /></WithPublicSkeleton>} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/onboarding" element={<OnboardingSetup />} />
              <Route path="/new-company" element={<NewCompany />} />
              <Route path="/book/:slug" element={<PublicBooking />} />
              <Route path="/book/:slug/embed" element={<PublicBookingEmbed />} />
              <Route path="/tire-hotel" element={<WithPublicSkeleton><TireHotelInfo /></WithPublicSkeleton>} />
              <Route path="/online-booking" element={<WithPublicSkeleton><BookingInfo /></WithPublicSkeleton>} />
              <Route path="/pricing" element={<WithPublicSkeleton><Pricing /></WithPublicSkeleton>} />
              <Route path="/checkout-success" element={<CheckoutSuccess />} />
              <Route path="/checkout-cancelled" element={<CheckoutCancelled />} />
              <Route path="/legal/data-request" element={<DataRequest />} />
              <Route path="/legal/:slug" element={<LegalPage />} />
              <Route path="/invitation" element={<Invitation />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/outreach-unsubscribe" element={<OutreachUnsubscribe />} />
              {/* Authenticated routes (with sidebar layout) */}
              <Route element={<AppLayout />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/companies" element={<Companies />} />
                
                <Route path="/customers" element={<Customers />} />
                <Route path="/storage" element={<Storage />} />
                <Route path="/storage/:id" element={<StorageDetail />} />
                <Route path="/tires" element={<TireSets />} />
                <Route path="/tires/:id" element={<TireSetDetail />} />
                <Route path="/bookings" element={<Bookings />} />
                <Route path="/booking-settings" element={<BookingSettings />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/admin" element={<AdminPanel />} />
                
                <Route path="/scanner" element={<Scanner />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/subscription-paused" element={<SubscriptionPaused />} />
              </Route>

              <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ChunkErrorBoundary>
          <CookieConsentBanner />
        </BrowserRouter>
        </TooltipProvider>
      </WorkshopProvider>
    </JurisdictionProvider>
  </QueryClientProvider>
);

export default App;
